import { ApolloClient, ApolloLink, createHttpLink, from, InMemoryCache } from '@apollo/client';
import { asyncMap } from '@apollo/client/utilities';
import { onError } from 'apollo-link-error';

import { ApplicationLocale } from '@ulta/core/providers/I18nProvider/I18nProvider';
import { fetchHeaderFooter } from '@ulta/core/utils/apolloMiddleware/fetchHeaderFooter/fetchHeaderFooter';
import { pageMiddleware } from '@ulta/core/utils/apolloMiddleware/pageMiddleware/pageMiddleware';
import { DXL_NAVIGATION_TYPE } from '@ulta/core/utils/constants/action';

import constants from '../constants/constants';
import { isServer } from '../device_detection/device_detection';
import { devLogger, LOG_TOPIC } from '../devMode/devMode';
import { getUrlParams } from '../domain/domain';
import { handleEmptyObjects } from '../handleEmptyObjects/handleEmptyObjects';
import { isInValidPath } from '../isInvalidPath/isInvalidPath';
import { updatedHeaderWithPageConfiguration } from '../updatedHeaderWithPageConfiguration/updatedHeaderWithPageConfiguration';
import { updatedFooterWithPageConfiguration } from '../updateFooterWithPageConfiguration/updatedFooterWithPageConfiguration';
import { updatePageResponse } from '../updatePageResponse/updatePageResponse';
import * as utils from './apollo_client';

const { HFN_CACHE_TIMEOUT = 300000 } = process?.env || {};

export let Apollo_Client;
export let Apollo_Config;
// cached storage for header/footer
let header, footer, footerSimple;
let HFN_TIMEOUT = 0;

const isExcluded = isInValidPath( ['/header', '/footer'] );

/**
 * A method that return an Apollo Error object to handle apollo graphql errors that may be encuntered
 *
 * @method
 * @param { Object } config - configuration object which contains the graphqlURI, ggraphqlDomain, and the resolvers object
 */
export const getApolloClient = ( config ) => {
  // persist this config in the closure so that we can access it later for swapping uris for get/post methods

  const {
    graphqlURI,
    graphqlDomain,
    previewDate,
    stagingHost,
    isStaging,
    locale,
    origin,
    isNative,
    connectToDevTools,
    fetch,
    requestHeaders,
    ssrMode,
    Apollo_State,
    ultaSite
  } = config;

  const ApolloCache = Apollo_State ? new InMemoryCache().restore( Apollo_State ) : new InMemoryCache();

  Apollo_Config = {
    graphqlURI,
    cache: ApolloCache,
    connectToDevTools,
    ssrMode,
    domain: graphqlDomain,
    uri: utils.extractApolloClientURI( graphqlURI, ( graphqlURI.enableGetForQueries ? 'GET' : 'POST' ) ),
    origin,
    isNative,
    name: 'ulta-graph',
    isStaging,
    stagingHost,
    previewDate,
    locale,
    requestHeaders,
    ultaSite,
    ...( fetch && { fetch } )
  };



  // only create apolloclient if one doesn't exist
  Apollo_Client = createApolloClient( Apollo_Config );

  return Apollo_Client;
};


export const extractApolloClientURI = ( graphqlURI, method ) => graphqlURI[ method ];

/**
 * Creates an ApolloClient instance which is used for conntectivity between the client
 * and the apollo graphql server.
 *
 * @method
 * @param { String } config - configuration object
 */
export const createApolloClient = config => {
  const { name, cache, connectToDevTools, stagingHost, previewDate, isStaging, origin, isNative, ssrMode, locale = ApplicationLocale } = config;

  return new ApolloClient(
    {
      ssrMode: ssrMode,
      connectToDevTools,
      link: createLinkChain( config ),
      credentials: 'include',
      name,
      cache
    }
  );
};

/**
 * @function getClientWithHttpLink
 * @summary Retrieves the Apollo Client instance and updates its link with a new HTTP link.
 * @description This function retrieves the existing Apollo Client instance using `utils.getClient()`.
 * It then updates the client's link by creating a new link chain using `createLinkChain`.
 * The new link chain is configured with the current `Apollo_Config` and a dynamically determined
 * URI based on the provided HTTP method (e.g., 'GET' or 'POST'). This allows the client to switch
 * between different endpoints or HTTP methods for different requests.
 * @param {string} method - The HTTP method to use ('GET' or 'POST'). This determines which URI
 *   from `Apollo_Config.graphqlURI` will be used.
 * @returns {ApolloClient} The Apollo Client instance with the updated link.
 * @throws {Error} Throws an error if `utils.getClient()` returns undefined, indicating that the
 *   Apollo Client has not been initialized.
 * @see {@link createLinkChain} for details on how the link chain is created.
 * @see {@link extractApolloClientURI} for details on how the URI is extracted.
 * @example
 * // Assuming Apollo_Config is already set up
 * const client = getClientWithHttpLink('GET');
 * // Now the client is configured to use the GET endpoint.
 *
 * const clientPost = getClientWithHttpLink('POST');
 * // Now the client is configured to use the POST endpoint.
 */
export const getClientWithHttpLink = ( method ) =>{

  let client = utils.getClient();
  client.setLink( createLinkChain( {
    ...Apollo_Config,
    uri: utils.extractApolloClientURI( Apollo_Config.graphqlURI, method )
  } ) );

  return client;
};

/**
 * @function createLinkChain
 * @summary Creates an Apollo Link chain from a configuration object.
 * @description This function constructs a chain of Apollo Links, including error handling,
 * authentication, header/footer appending, HFN middleware, page middleware, content replacement
 * determination, and the HTTP link. It uses the `from` function from `@apollo/client` to combine
 * these links into a single, executable link.
 * @param {object} config - The configuration object.
 * @param {string} config.name - The name of the Apollo client.
 * @param {InMemoryCache} config.cache - The Apollo in-memory cache instance.
 * @param {boolean} config.connectToDevTools - Whether to connect to Apollo DevTools.
 * @param {string} config.stagingHost - The staging host URL.
 * @param {string} config.previewDate - The preview date for content.
 * @param {boolean} config.isStaging - Whether the environment is staging.
 * @param {string} config.origin - The origin URL.
 * @param {boolean} config.isNative - Whether the client is a native app.
 * @param {boolean} config.ssrMode - Whether the application is in server-side rendering mode.
 * @param {string} [config.locale=ApplicationLocale] - The application locale.
 * @returns {ApolloLink} An Apollo Link chain.
 */
export const createLinkChain = ( config ) => {
  const { name, cache, connectToDevTools, stagingHost, previewDate, isStaging, origin, isNative, ssrMode, locale = ApplicationLocale, ultaSite, requestHeaders } = config;

  return from( [
    onError( onErrorHandler( console ) ),
    getAuthMiddleWare( { stagingHost, previewDate, isNative, ultaSite, requestHeaders } ),
    new ApolloLink( appendHeaderFooter( { isStaging }, { getHFN } ) ),
    new ApolloLink( hfnMiddleware( { isStaging, origin, config: Apollo_Config, locale }, {
      getCacheTimeout,
      updateCacheTimeout,
      setHFN,
      getHFN
    } ) ),
    new ApolloLink( pageMiddleware( { previewDate, isStaging } ) ),
    new ApolloLink( determineContentReplace ),
    getHttpLink( config )
  ] );
};

/**
 * Determines whether to replace the content
 * based on initAction content.
 *
 * @method
 * @param { method } operation
 * @param { method } forward
 * @returns ApolloLink
 */
export const determineContentReplace = ( operation, forward ) => {
  return forward( operation ).map( ( response ) => {
    const { meta, content } = response?.data?.Page || {};
    const initAction = meta?.initAction || {};

    const isContentReplace =
      content === null && !!initAction.content && initAction.navigationType === DXL_NAVIGATION_TYPE.Push;

    operation.setContext( { response, isContentReplace } );

    return response;
  } );
};

/**
 * Error handler method for the apollo-link-error middleware which inspects
 * the existance of a graphql error or a network error  on all requests and outputs them all
 * to the console
 *
 * @method
 * @param { Object } logger - Instance of a logger object used to log messages
 */
export const onErrorHandler = ( logger = console ) => {
  return ( { graphQLErrors, networkError, operation, forward, response } ) => {
    if( graphQLErrors ){
      graphQLErrors.forEach( ( { message, locations, path } ) => {
        logger.log( `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}` );
      } );
    }
    if( networkError ){
      logger.log( response );
      logger.log( `[Network error]: ${networkError}` );
    }
    forward( operation );
  };
};

/**
 * returns a curried Method which is responsible for setting global request auth headers
 * for the apollo client instance
 *
 * @method
 * @param { object } data -  object which contains locale value,previewDate, and stagingHost which should be passed to the header configuration
 * @param data.locale - locale value passed to header config
 * @param data.stagingHost - staging host value
 * @param data.previewDate - preview date value
 * @param data.isNative - is native value
 */
export const setAuthHeaders = ( data ) => {
  const { previewDate, stagingHost, locale, isNative, ultaSite, requestHeaders = {} } = data;
  return ( { headers = {} } ) => {
    return {
      headers: {
        ...headers,
        ...( requestHeaders ),
        'X-ULTA-CLIENT-COUNTRY': 'US',
        'X-ULTA-CLIENT-LOCALE': locale,
        'X-ULTA-CLIENT-CHANNEL': 'web',
        ...( !!previewDate && { 'X-ULTA-CLIENT-PREVIEWDATETIME': previewDate } ),
        ...( !!stagingHost && { 'X-ULTA-CLIENT-STAGINGHOST': stagingHost } ),
        ...( !!ultaSite && { 'X_ULTA_SITE': ultaSite } ),
        ...( !!isNative && { 'X-ULTA-CLIENT-ISNATIVE': isNative } ),
        'X-FORWARDED-PROTO': 'https'
      }
    };
  };
};

/**
 * Error handler method for the apollo-link-error which inspects
 * the existance of a graphql error or a network error and outputs
 * it to the console for viewing
 *
 * @method
 * @param { String } data - the locale value which should be passed to the header configuration
 * @param { String } data.stagingHost - the staging host Value
 * @param { String } data.previewDate - the previewDate value
 * @param { String } data.isNative - the is native value
 */
export const getAuthMiddleWare = ( data ) => {
  const { stagingHost, previewDate, isNative, ultaSite, requestHeaders } = data || {};
  return new ApolloLink( getAuthMiddlewareLinkCallBack( { stagingHost, previewDate, isNative, ultaSite, requestHeaders } ) );
};

/**
 * Error handler method for the apollo-link-error which inspects
 * the existance of a graphql error or a network error and outputs
 * it to the console for viewing
 *
 * @method
 * @param { String } data - object containg data  to be passed into the method
 * @param { String } data.stagingHost - the staging host Value
 * @param { String } data.previewDate - the previewDate
 * @param { String } data.isNative - the is native value
 */
export const getAuthMiddlewareLinkCallBack = ( data ) => {
  const { stagingHost: serverStagingHost, previewDate: serverPreviewDate, isNative: serverIsNative, ultaSite, requestHeaders } = data;

  return ( operation, forward ) => {
    let locale = ApplicationLocale;
    const operationVariables = operation.variables || {};

    const {
      stagingHost: variablesStagingHost,
      previewDate: variablesPreviewDate,
      moduleParams,
      isNative: variablesIsNative
    } = operationVariables || {};

    const stagingHost = ( variablesStagingHost || serverStagingHost ) ?? undefined;
    const previewDate = ( variablesPreviewDate || serverPreviewDate ) ?? undefined;
    const isNative = ( variablesIsNative || serverIsNative ) ?? undefined;
    operation.setContext( setAuthHeaders( { locale, stagingHost, previewDate, isNative, ultaSite, requestHeaders } ) );
    operation.setContext( setUserData( moduleParams ) );
    operation.setContext( { isNative: isNative } );

    return forward( operation );
  };
};

/**
 * Error handler method for the apollo-link-error which inspects
 * the existance of a graphql error or a network error and outputs
 * it to the console for viewing
 *
 * @method
 * @param { String } locale - the locale value which should be passed to the header configuration
 */
export const getHttpLink = ( config ) => {
  return createHttpLink( {
    ...( config.fetch && { fetch: config.fetch } ),
    ...( isServer() && config.fetchOptions && { fetchOptions: config.fetchOptions } ),
    uri: `${config.domain}/${config.uri}`,
    credentials: 'include'
  } );
};

/**
 * setUserData accepts a data object and pulls out the login status and gti
 * @param { object } data
 * @returns object - user
 */
// TODO - move to utilites file
export const setUserData = ( data ) => {
  const { loginStatus = 'anonymous', gti = '' } = data || {};

  return {
    user: {
      loginStatus,
      gti
    }
  };
};

/**
 * getClient is a helper method to retrieve the apollo_clien t instance that was created at application hydration
 * @method
 * @returns ApolloClient
 */
export const getClient = function( ){
  return Apollo_Client;
};

/*
 * TODO: Middleware to move out of this file
 */

/**
 * appendHeaderFooter will update a Page Response with Header and Footer data
 * @method
 * @param { method } operation
 * @param { method } forward
 * @returns ApolloLink
 */
export const appendHeaderFooter = ( data, methods ) => {
  const { isStaging } = data;
  const { getHFN } = methods || {};

  return function( operation, forward ){
    return forward( operation ).map( ( response ) => {
      const {
        web = {},
        isPage = false,
        isNative = false,
        enableGlobalHFNForDSOTF,
        isContentReplace
      } = operation.getContext() || {};
      let { header, footer, footerSimple } = getHFN( { isStaging } );
      const isValidPage = isValidPath( operation.variables?.url );

      const pageContent = response?.data?.Page?.content || {};
      const isPageRequest = isPage && isValidPage;
      const shouldAppend = isContentReplace || isPageRequest;

      if( shouldAppend && !isNative && enableGlobalHFNForDSOTF && header && footer ){
        /*
         * if header and footer are cached and the operation is a page operation, update the page response with a new HFN
         */
        operation.setContext( { isPage: false } );
        return updatePageResponse( {
          response,
          header: updatedHeaderWithPageConfiguration( {
            header,
            configuration: web
          } ),
          footer: updatedFooterWithPageConfiguration( {
            footer: web.displaySimplifiedFooter ? footerSimple : footer,
            configuration: web
          } ),
          pageModules: pageContent.modules
        } );
      }

      return response;
    } );
  };
};

/**
 * HFN Middleware is a wrapper for ease of testing
 * This method uses the operation and forward from ApolloLink and sends it to asyncMap
 * So that we can make addition async calls to fetch the header and footer from dxl
 * @param {*} methods - curried methods from ApolloClient
 * @returns fetchHeaderFooter
 */
export const hfnMiddleware = ( data, methods ) => {
  return ( operation, forward ) => {
    return asyncMap( forward( operation ), async( response ) => fetchHeaderFooter( response, methods, data, operation ) );
  };
};

/**
 * Helper getter and setter methods for curried ApolloLink functions
 */
export const getHFN = () => {
  return { header, footer, footerSimple };
};

export const setHFN = ( data = {} ) => {
  if( data.header ){
    devLogger( 'HFN cache updated for header', 0, LOG_TOPIC.Cache );
    header = data.header;
  }
  if( data.footer ){
    devLogger( 'HFN cache updated for footer', 0, LOG_TOPIC.Cache );
    footer = data.footer;
  }
  if( data.footerSimple ){
    devLogger( 'HFN cache updated for footerSimple', 0, LOG_TOPIC.Cache );
    footerSimple = data.footerSimple;
  }
  return { header, footer, footerSimple };
};

export const getCacheTimeout = () => {
  return HFN_TIMEOUT;
};

export const updateCacheTimeout = ( timeout = HFN_CACHE_TIMEOUT ) => {
  HFN_TIMEOUT = new Date( Date.now() + Number( timeout ) ).getTime();
  return HFN_TIMEOUT;
};

export const isValidPath = ( data, methods ) => {
  const { path } = data || {};
  const { excluded = isExcluded } = methods || {};

  if( path && excluded( path ) ){
    return false;
  }
  else {
    return true;
  }
};

export const isApolloStateMismatch = () => {
  if( isServer() ){
    return false;
  }

  const { ROOT_QUERY } = handleEmptyObjects( global.__APOLLO_STATE__ );
  const keys = ( ROOT_QUERY && Object.keys( ROOT_QUERY ) ) || [];

  const params = getUrlParams( global.location.search );

  let cacheKey = '';
  for ( const key of keys ){
    if( key.includes( 'Page(' ) ){
      cacheKey = key;
      break;
    }
  }

  let moduleParamMismatch = false;
  Object.entries( params ).forEach( ( [k, v] ) => {
    if( !cacheKey.includes( `"${k}"` ) || !cacheKey.includes( `"${v}"` ) ){
      moduleParamMismatch = true;
    }
  } );

  return moduleParamMismatch;
};

export const getApolloState = () => {
  const { ROOT_QUERY } = handleEmptyObjects( global.__APOLLO_STATE__ );
  const rootQueryList = ( ROOT_QUERY && Object.entries( ROOT_QUERY ) ) || [];

  let initialPageData = null;
  for ( const query of rootQueryList ){
    const [key, Page] = query;
    if( key.includes( 'Page(' ) ){
      if( utils.pageDataIgnoreList( Page.meta?.url ) ){
        continue;
      }
      initialPageData = { Page };
      break;
    }
  }

  return JSON.parse( JSON.stringify( initialPageData ) );
};

/**
 * Skip these endpoints
 * @param {string} url
 * @returns {boolean}
 */
export const pageDataIgnoreList = ( url ) =>
  url && [constants.HFN.header, constants.HFN.footer].some( ( p ) => url.includes( p ) );
